import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Filter, Bot, User, ShoppingBag, Clock, MessageCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

function SalesChatList({ chats, setChats, selectedChat, onSelectChat, loading, setLoading }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const listRef = useRef(null);
  const limit = 20;

  // Initial fetch
  useEffect(() => {
    fetchChats(0, true);
  }, [filterStatus]);

  const fetchChats = async (currentOffset, isInitial = false) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = new URLSearchParams({
        status: filterStatus,
        limit: limit,
        offset: currentOffset
      });

      const response = await fetch(`${API_URL}/api/sales/chats?${params}`);
      const data = await response.json();

      if (isInitial) {
        setChats(data.chats);
      } else {
        setChats(prev => [...prev, ...data.chats]);
      }

      setHasMore(data.chats.length === limit);
      setOffset(currentOffset + data.chats.length);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Infinite scroll
  const handleScroll = useCallback(() => {
    if (!listRef.current || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      fetchChats(offset, false);
    }
  }, [offset, loadingMore, hasMore]);

  useEffect(() => {
    const list = listRef.current;
    if (list) {
      list.addEventListener('scroll', handleScroll);
      return () => list.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const filteredChats = chats.filter(chat => {
    const matchesSearch = 
      chat.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      chat.phone_number.includes(searchQuery);
    return matchesSearch;
  });

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins}m`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d`;
  };

  const getStageColor = (stage) => {
    const colors = {
      'initial': 'bg-gray-100 text-gray-700',
      'product_inquiry': 'bg-blue-100 text-blue-700',
      'product_selection': 'bg-purple-100 text-purple-700',
      'address_collection': 'bg-orange-100 text-orange-700',
      'order_placed': 'bg-green-100 text-green-700',
      'completed': 'bg-emerald-100 text-emerald-700',
      'escalated': 'bg-red-100 text-red-700'
    };
    return colors[stage] || 'bg-gray-100 text-gray-700';
  };

  return (
    <>
      {/* Search and Filter */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o teléfono..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div className="flex space-x-2">
          {[
            { value: 'all', label: 'Todos' },
            { value: 'active', label: 'Activos' },
            { value: 'needs_attention', label: 'Atención' },
            { value: 'completed', label: 'Completados' }
          ].map(filter => (
            <button
              key={filter.value}
              onClick={() => setFilterStatus(filter.value)}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                filterStatus === filter.value
                  ? 'bg-[#B1336E] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chat List */}
      <div ref={listRef} className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B1336E] mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Cargando chats...</p>
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="p-8 text-center">
            <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No hay chats</p>
          </div>
        ) : (
          <>
            {filteredChats.map(chat => (
              <div
                key={chat.id}
                onClick={() => onSelectChat(chat)}
                className={`p-4 border-b border-gray-200 cursor-pointer transition-colors ${
                  selectedChat?.id === chat.id
                    ? 'bg-purple-50 border-l-4 border-l-[#B1336E]'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {chat.customer_name || chat.phone_number}
                      </h3>
                      {chat.unread && (
                        <div className="w-2 h-2 bg-[#B1336E] rounded-full"></div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{chat.phone_number}</p>
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    <span className="text-xs text-gray-500">
                      {getTimeAgo(chat.last_message_at)}
                    </span>
                    {chat.is_bot_active ? (
                      <Bot className="w-4 h-4 text-green-600" />
                    ) : (
                      <User className="w-4 h-4 text-purple-600" />
                    )}
                  </div>
                </div>

                <p className="text-sm text-gray-600 truncate mb-2">
                  {chat.last_message || 'Sin mensajes'}
                </p>

                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${getStageColor(chat.conversation_stage)}`}>
                    {chat.conversation_stage}
                  </span>
                  {chat.has_order && (
                    <ShoppingBag className="w-4 h-4 text-green-600" />
                  )}
                </div>
              </div>
            ))}

            {loadingMore && (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#B1336E] mx-auto"></div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default SalesChatList;